import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { addWeeks } from 'date-fns'

import ContentSection from '../../../../../../../features/components/ContentSection'
import ContentRow from '../../../../../../../features/components/ContentSection/ContentRow'
import MultiRowFields from '../../../../../../../features/components/Form/MultiRowFields'
import BookedMediaCustomDatePicker from './BookedMediaCustomDatePicker'

import { CUSTOM_DATES, DATE } from '../fields'

import useStyles from './styles'

const UpdateBookedMediaCustomDatesFormContent = ({ formik, bookedMediaDateStart }) => {
  const classes = useStyles()

  const { values } = formik

  const initialValue = useMemo(() => {
    // Retrieve the custom dates, defaulting to an empty array.
    const customDates = values[CUSTOM_DATES] || []

    // When no custom dates exist, default to the booked_media start date.
    if (customDates.length === 0) {
      return { [DATE]: new Date(bookedMediaDateStart) }
    }

    // When only one date exists, default the next date to one week after the first.
    if (customDates.length === 1) {
      const firstDate = new Date(customDates[0][DATE])
      const nextDate = addWeeks(firstDate, 1)
      return { [DATE]: nextDate }
    }

    // For two or more dates, use the gap between the last two dates.
    if (customDates.length >= 2) {
      const len = customDates.length
      const previousDate = new Date(customDates[len - 2][DATE])
      const lastDate = new Date(customDates[len - 1][DATE])

      // Calculate the gap in milliseconds.
      const gap = lastDate.getTime() - previousDate.getTime()

      // Ensure the gap is positive before computing the next date.
      if (gap > 0) {
        const nextDate = new Date(lastDate.getTime() + gap)
        return { [DATE]: nextDate }
      }
    }

    // Default fallback to the current date.
    return { [DATE]: new Date() }
  }, [values, bookedMediaDateStart])

  return (
    <ContentSection title="Custom dates">
      <ContentRow
        title="Custom file upload dates"
        description="Files will be requested for the following dates. Creative deadline dates will be calculated accordingly and file upload reminders sent for each date separately."
      >
        <MultiRowFields
          formik={formik}
          FieldsComponent={BookedMediaCustomDatePicker}
          initialValueTemplate={initialValue}
          mainValueName={CUSTOM_DATES}
          subValueNames={[DATE]}
          addNewRowText="+ File upload date"
          className={classes.datePickersList}
          minRows={0}
        />
      </ContentRow>
    </ContentSection>
  )
}

UpdateBookedMediaCustomDatesFormContent.propTypes = {
  formik: PropTypes.object.isRequired,
  bookedMediaDateStart: PropTypes.string.isRequired
}

export default UpdateBookedMediaCustomDatesFormContent
